import React from "react";
import { useTranslation } from "react-i18next";
import { SchoolDashboard } from "../../common/api";
import {
  DataTable,
  LabelWithTooltip,
  useSortableColumns,
} from "./DashboardStyles";

const MINIMUM_SUGGESTED_TUTORING_TIME_MINUTES = 20;
const MINIMUM_SUGGESTED_TUTORING_TIME_SECONDS =
  MINIMUM_SUGGESTED_TUTORING_TIME_MINUTES * 60;

type SortKey =
  | "name"
  | "this_week_tutoring_seconds"
  | "last_week_tutoring_seconds"
  | "total_tutoring_seconds"
  | "total_lessons_completed";

export const TeacherStudentsTab = ({
  dashboard,
  navigate,
}: {
  dashboard: SchoolDashboard &
    ({ is_at_teacher_level: true } | { is_at_school_group_level: true });
  navigate: (string) => void;
}) => {
  const { t } = useTranslation();
  const { thProps, indicator, sortRows } = useSortableColumns<SortKey>();

  const hasWeeklyColumns =
    dashboard.user_info.length > 0 &&
    "this_week_tutoring_seconds" in dashboard.user_info[0];

  const sorted = sortRows<(typeof dashboard.user_info)[number]>(
    dashboard.user_info,
    (a, b, key) => {
      if (key === "name") {
        const an = a.first_name + " " + (a.last_name ?? "");
        const bn = b.first_name + " " + (b.last_name ?? "");
        return an.localeCompare(bn);
      }
      return ((a as any)[key] ?? 0) - ((b as any)[key] ?? 0);
    }
  );

  const usageTooltip = t("dashboard_usage_recomendation_tooltip", {
    suggested_tutoring_time_minutes: MINIMUM_SUGGESTED_TUTORING_TIME_MINUTES,
  });

  return (
    <DataTable>
      <thead>
        <tr>
          <th {...thProps("name")}>
            {t("dashboard_column_student_name")}
            {indicator("name")}
          </th>
          {hasWeeklyColumns && (
            <>
              <th {...thProps("this_week_tutoring_seconds")}>
                <LabelWithTooltip tooltip={usageTooltip}>
                  {t("dashboard_column_this_weeks_usage")}
                </LabelWithTooltip>
                {indicator("this_week_tutoring_seconds")}
              </th>
              <th {...thProps("last_week_tutoring_seconds")}>
                <LabelWithTooltip tooltip={usageTooltip}>
                  {t("dashboard_column_last_weeks_usage")}
                </LabelWithTooltip>
                {indicator("last_week_tutoring_seconds")}
              </th>
            </>
          )}
          <th {...thProps("total_tutoring_seconds")}>
            {t("dashboard_column_total_usage")}
            {indicator("total_tutoring_seconds")}
          </th>
          <th {...thProps("total_lessons_completed")}>
            {t("dashboard_column_lessons_completed")}
            {indicator("total_lessons_completed")}
          </th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((user, index) => (
          <tr key={index}>
            <td>
              <a href={user.url} onClick={() => navigate(user.url)}>
                {user.first_name}
                {user.last_name && " " + user.last_name}
              </a>
            </td>
            {hasWeeklyColumns && (
              <>
                <td
                  className={
                    (user.this_week_tutoring_seconds || 0) <
                    MINIMUM_SUGGESTED_TUTORING_TIME_SECONDS
                      ? undefined
                      : "text-success"
                  }
                >
                  {formatSecondsAsHourMinute(
                    user.this_week_tutoring_seconds ?? 0
                  )}
                </td>
                <td
                  className={
                    (user.last_week_tutoring_seconds || 0) <
                    MINIMUM_SUGGESTED_TUTORING_TIME_SECONDS
                      ? "text-danger"
                      : "text-success"
                  }
                >
                  {formatSecondsAsHourMinute(
                    user.last_week_tutoring_seconds ?? 0
                  )}
                </td>
              </>
            )}
            <td>{formatSecondsAsHourMinute(user.total_tutoring_seconds)}</td>
            <td>{user.total_lessons_completed || 0}</td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
};

function formatSecondsAsHourMinute(totalSeconds: number) {
  totalSeconds = totalSeconds || 0;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
}
